import { Card, CardHeader, CardContent, Button, Typography, Box, FormControl, InputLabel, Select, MenuItem, Chip, OutlinedInput, Avatar, Stack, Tabs, Tab } from "@mui/material";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import { useCallback, useEffect, useMemo, useState } from "react";
import { PostForMeAccount, DEFAULT_CONFIGS, PlatformKey, TabKey, PlatformOverrideState, PlatformConfiguration, deriveSelectedPlatforms, PLATFORM_LIMITS, getEffectiveForPlatform, EffectiveValues } from "./models";
import FacebookTab from "./Tabs/FacebookTab";
import InstagramTab from "./Tabs/InstagramTab";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import DraftTab from "./Tabs/DraftTab";
import { buildCreatePostBody } from "./util";
import TikTokTab from "./Tabs/TikTokTab";

type Props = {
  accounts: PostForMeAccount[];
  selectedAccountIds: string[];
  setSelectedAccountIds: (ids: string[]) => void;
  postError?: string | null;
  postSuccess?: string | null;
  submitPost: (body: any) => void;
  onPreviewChange?: (previews: Partial<Record<PlatformKey, EffectiveValues>>) => void;
};

const PLATFORM_CONFIG: Record<string, { label: string; maxLength: number; icon: (p?: any) => JSX.Element }> = {
  facebook: {
    label: "Facebook",
    maxLength: PLATFORM_LIMITS.facebook,
    icon: (p) => <FacebookIcon {...p} color="primary" />
  },
  instagram: {
    label: "Instagram",
    maxLength: PLATFORM_LIMITS.instagram,
    icon: (p) => <InstagramIcon {...p} color="error" />
  },
  tiktok: {
    label: "TikTok",
    maxLength: PLATFORM_LIMITS.tiktok,
    icon: (p) => <MusicNoteIcon {...p} color="secondary" />
  }
};

function getPlatformIcon(platform: string) {
  const key = platform.toLowerCase();
  const cfg = PLATFORM_CONFIG[key];
  if (cfg) {
    return cfg.icon();
  } else {
    return <></>;
  }
}

export function PostCreateForm({
  accounts,
  selectedAccountIds,
  setSelectedAccountIds,
  postError,
  postSuccess,
  submitPost,
  onPreviewChange
}: Props) {
  const [draftCaption, setDraftCaption] = useState<string>("");
  const [draftMedia, setDraftMedia] = useState<string[]>([]);
  const [draftHasPhoto, setDraftHasPhoto] = useState(false);
  const [draftHasVideo, setDraftHasVideo] = useState(false);
  const [overrides, setOverrides] = useState<Partial<Record<PlatformKey, PlatformOverrideState>>>({});
  const [activeTab, setActiveTab] = useState<TabKey>("draft");

  const selectedPlatforms = useMemo(
    () => deriveSelectedPlatforms(accounts, selectedAccountIds),
    [accounts, selectedAccountIds]
  );

  useEffect(() => {
    if (activeTab !== "draft" && !selectedPlatforms.includes(activeTab as PlatformKey)) {
      setActiveTab("draft");
    }
  }, [selectedPlatforms, activeTab]);

  const handleSelectChange = (e: any) => {
    const value: string[] = e.target.value;
    setSelectedAccountIds(value);
  };

  const getPlatformConfig = (p: PlatformKey) => ({
    maxLength: PLATFORM_CONFIG[p]?.maxLength ?? 0,
    icon: PLATFORM_CONFIG[p]?.icon ?? (() => <></>)
  });

  const updateOverride = useCallback(
    (platform: PlatformKey, mutator: (prev: PlatformOverrideState) => PlatformOverrideState) => {
      setOverrides(prev => {
        const current: PlatformOverrideState = prev[platform]
          ? {
            caption: prev[platform]?.caption,
            captionTouched: prev[platform]?.captionTouched,
            media: prev[platform]?.media,
            mediaTouched: prev[platform]?.mediaTouched,
            config: { ...prev[platform]!.config },
            hasPhoto: prev[platform]?.hasPhoto,
            hasVideo: prev[platform]?.hasVideo
          }
          : { config: { ...DEFAULT_CONFIGS[platform] } as PlatformConfiguration };
        const next = mutator(current);
        return { ...prev, [platform]: next };
      });
    },
    []
  );

  const effectiveCaption = useCallback(
    (platform: PlatformKey | "draft") => {
      if (platform === "draft") return draftCaption;
      const o = overrides[platform as PlatformKey];
      return o?.caption !== undefined ? o.caption ?? "" : draftCaption;
    },
    [draftCaption, overrides]
  );

  const effectiveMedia = useCallback(
    (platform: PlatformKey | "draft") => {
      if (platform === "draft") return draftMedia;
      const o = overrides[platform as PlatformKey];
      const useOverride = !!o?.mediaTouched && Array.isArray(o?.media);
      return useOverride ? (o!.media as string[]) : draftMedia;
    },
    [draftMedia, overrides]
  );

  const effectiveMediaKinds = useCallback(
    (platform: PlatformKey | "draft") => {
      if (platform === "draft") return { hasPhoto: draftHasPhoto, hasVideo: draftHasVideo };
      const o = overrides[platform as PlatformKey];
      return {
        hasPhoto: o?.hasPhoto ?? draftHasPhoto,
        hasVideo: o?.hasVideo ?? draftHasVideo
      };
    },
    [draftHasPhoto, draftHasVideo, overrides]
  );

  const platformConfig = (platform: PlatformKey) =>
    (overrides[platform]?.config ?? DEFAULT_CONFIGS[platform]) as PlatformConfiguration;

  const isPlatformValid = (platform: PlatformKey) => {
    const eff = getEffectiveForPlatform(
      {
        draft: { caption: draftCaption, media: draftMedia, hasPhoto: draftHasPhoto, hasVideo: draftHasVideo },
        platforms: overrides,
        selectedAccountIds
      },
      platform
    );
    const lengthLimit = PLATFORM_LIMITS[platform];

    if ((platform === "instagram" || platform === "tiktok") && eff.media.length === 0) return false;

    if (eff.placement === "timeline") {
      if (platform === "facebook" && eff.media.length > 0) {
        if (eff.hasPhoto && eff.hasVideo) return false;
      }
      const hasCaption = eff.caption.trim().length > 0;
      const hasMedia = eff.media.length > 0;
      if (!hasCaption && !hasMedia) return false;
      if (hasCaption && eff.caption.length > lengthLimit) return false;
      return true;
    }

    return eff.media.length > 0;
  };

  const anyOverLimitTimeline = useMemo(() => {
    return selectedPlatforms.some((p) => {
      const eff = getEffectiveForPlatform(
        { draft: { caption: draftCaption, media: draftMedia }, platforms: overrides, selectedAccountIds },
        p
      );
      if (eff.placement !== "timeline") return false;
      return eff.caption.length > PLATFORM_LIMITS[p];
    });
  }, [draftCaption, draftMedia, overrides, selectedAccountIds, selectedPlatforms]);

  const disableSubmit =
    selectedAccountIds.length === 0 ||
    selectedPlatforms.length === 0 ||
    anyOverLimitTimeline ||
    selectedPlatforms.some((p) => !isPlatformValid(p));

  const buildBody = () =>
    buildCreatePostBody(
      {
        draft: { caption: draftCaption, media: draftMedia },
        platforms: overrides,
        selectedAccountIds
      },
      accounts
    );

  const captionIgnoredWarning = (platform: PlatformKey) => {
    const eff = getEffectiveForPlatform(
      { draft: { caption: draftCaption, media: draftMedia, hasPhoto: draftHasPhoto, hasVideo: draftHasVideo }, platforms: overrides, selectedAccountIds },
      platform
    );
    return eff.placement !== "timeline" && eff.caption.trim().length > 0;
  };

  useEffect(() => {
    if (!onPreviewChange) return;
    const out: Partial<Record<PlatformKey, EffectiveValues>> = {};
    if (selectedPlatforms.includes("facebook")) {
      out.facebook = getEffectiveForPlatform(
        { draft: { caption: draftCaption, media: draftMedia, hasPhoto: draftHasPhoto, hasVideo: draftHasVideo }, platforms: overrides, selectedAccountIds },
        "facebook"
      );
    }
    if (selectedPlatforms.includes("instagram")) {
      out.instagram = getEffectiveForPlatform(
        { draft: { caption: draftCaption, media: draftMedia, hasPhoto: draftHasPhoto, hasVideo: draftHasVideo }, platforms: overrides, selectedAccountIds },
        "instagram"
      );
    }
    if (selectedPlatforms.includes("tiktok")) {
      out.tiktok = getEffectiveForPlatform(
        { draft: { caption: draftCaption, media: draftMedia, hasPhoto: draftHasPhoto, hasVideo: draftHasVideo }, platforms: overrides, selectedAccountIds },
        "tiktok"
      );
    }
    onPreviewChange(out);
  }, [onPreviewChange, selectedPlatforms, draftCaption, draftMedia, draftHasPhoto, draftHasVideo, overrides, selectedAccountIds]);

  const renderTabPanel = (tab: TabKey) => {
    const isDraft = tab === "draft";
    if (isDraft) {
      return (
        <Box key="draft" role="tabpanel" hidden={activeTab !== "draft"} sx={styles.tabPanel}>
          {activeTab === "draft" && (
            <DraftTab
              caption={draftCaption}
              onCaptionChange={setDraftCaption}
              selectedPlatforms={selectedPlatforms}
              getPlatformConfig={getPlatformConfig}
              mediaUrls={draftMedia}
              setMediaUrls={setDraftMedia}
              onMediaKindsChange={({ hasPhoto, hasVideo }) => {
                setDraftHasPhoto(hasPhoto);
                setDraftHasVideo(hasVideo);
              }}
            />
          )}
        </Box>
      );
    }

    const platformKey = tab as PlatformKey;
    const cfg = platformConfig(platformKey);
    const currentCaption = effectiveCaption(platformKey);
    const currentMedia = effectiveMedia(platformKey);
    const maxLength = PLATFORM_CONFIG[platformKey]?.maxLength ?? 0;

    const { hasPhoto: currentHasPhoto, hasVideo: currentHasVideo } = effectiveMediaKinds(platformKey);

    return (
      <Box key={platformKey} role="tabpanel" hidden={activeTab !== platformKey} sx={styles.tabPanel}>
        {activeTab === platformKey && (
          <>
            {(() => {
              if (platformKey === "facebook") {
                return (
                  <FacebookTab
                    caption={currentCaption}
                    onCaptionChange={(value) =>
                      updateOverride("facebook", prev => ({ ...prev, caption: value, captionTouched: true }))
                    }
                    media={currentMedia}
                    onMediaChange={(urls) =>
                      updateOverride("facebook", prev => ({ ...prev, media: urls, mediaTouched: true }))
                    }
                    config={cfg as any}
                    onConfigChange={(newCfg) =>
                      updateOverride("facebook", prev => ({ ...prev, config: newCfg }))
                    }
                    maxLength={maxLength}
                    onMediaKindsChange={({ hasPhoto, hasVideo }) =>
                      updateOverride("facebook", prev => ({ ...prev, hasPhoto, hasVideo }))
                    }
                    hasPhoto={currentHasPhoto}
                    hasVideo={currentHasVideo}
                  />
                );
              }
              if (platformKey === "instagram") {
                return (
                  <InstagramTab
                    caption={currentCaption}
                    onCaptionChange={(value) =>
                      updateOverride("instagram", prev => ({ ...prev, caption: value, captionTouched: true }))
                    }
                    media={currentMedia}
                    onMediaChange={(urls) =>
                      updateOverride("instagram", prev => ({ ...prev, media: urls, mediaTouched: true }))
                    }
                    config={cfg as any}
                    onConfigChange={(newCfg) =>
                      updateOverride("instagram", prev => ({ ...prev, config: newCfg }))
                    }
                    maxLength={maxLength}
                    onMediaKindsChange={({ hasPhoto, hasVideo }) =>
                      updateOverride("instagram", prev => ({ ...prev, hasPhoto, hasVideo }))
                    }
                    hasPhoto={currentHasPhoto}
                    hasVideo={currentHasVideo}
                  />
                );
              }
              return (
                <TikTokTab
                  caption={currentCaption}
                  onCaptionChange={(value) =>
                    updateOverride("tiktok", prev => ({ ...prev, caption: value, captionTouched: true }))
                  }
                  media={currentMedia}
                  onMediaChange={(urls) =>
                    updateOverride("tiktok", prev => ({ ...prev, media: urls, mediaTouched: true }))
                  }
                  config={cfg as any}
                  onConfigChange={(newCfg) =>
                    updateOverride("tiktok", prev => ({ ...prev, config: newCfg }))
                  }
                  maxLength={maxLength}
                  onMediaKindsChange={({ hasPhoto, hasVideo }) =>
                    updateOverride("tiktok", prev => ({ ...prev, hasPhoto, hasVideo }))
                  }
                />
              );
            })()}
          </>
        )}
      </Box>
    );
  };

  return (
    <Card>
      <CardHeader title="Create a post" />
      <CardContent sx={styles.cardContent}>
        <FormControl fullWidth size="small">
          <InputLabel id="pfm-accounts-label">Select Accounts</InputLabel>
          <Select
            labelId="pfm-accounts-label"
            multiple
            value={selectedAccountIds}
            onChange={handleSelectChange}
            input={<OutlinedInput label="Select Accounts" />}
            renderValue={selected => (
              <Box sx={styles.renderValueBox}>
                {selected.map(id => {
                  const acc = accounts.find(a => a.id === id);
                  if (!acc) return null;
                  const platform = acc.platform;
                  return (
                    <Chip
                      key={id}
                      avatar={
                        <Avatar variant="rounded" sx={styles.chipAvatar}>
                          {getPlatformIcon(platform)}
                        </Avatar>
                      }
                      label={acc.username}
                      size="small"
                      sx={styles.accountChip}
                    />
                  );
                })}
              </Box>
            )}
            disabled={accounts.length === 0}
          >
            {accounts.map(acc => {
              const platform = acc.platform;
              return (
                <MenuItem key={acc.id} value={acc.id}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {getPlatformIcon(platform)}
                    <Typography variant="body2" fontWeight={500} noWrap>
                      {acc.username}
                    </Typography>
                  </Stack>
                </MenuItem>
              );
            })}
            {accounts.length === 0 && (
              <MenuItem disabled value="__none">
                No accounts connected
              </MenuItem>
            )}
          </Select>
        </FormControl>

        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value as TabKey)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Draft" value="draft" />
          {selectedPlatforms.map(platform => {
            const hasIssue = !isPlatformValid(platform);
            const labelText = ({ facebook: "Facebook", instagram: "Instagram", tiktok: "TikTok" } as const)[platform] ?? platform;
            return (
              <Tab
                key={platform}
                value={platform}
                label={labelText}
                iconPosition="start"
                icon={PLATFORM_CONFIG[platform]?.icon({ fontSize: "small" })}
                sx={hasIssue ? styles.tabWarnDot : undefined}
                aria-label={hasIssue ? `${labelText} has issues` : undefined}
              />
            );
          })}
        </Tabs>

        {["draft", ...selectedPlatforms].map(tab => renderTabPanel(tab as TabKey))}

        {postError && <Typography variant="subtitle2" color="error">{postError}</Typography>}
        {postSuccess && <Typography variant="subtitle2" color="success.main">{postSuccess}</Typography>}

        <Box sx={styles.submitActions}>
          <Button
            variant="contained"
            onClick={() => submitPost(buildBody())}
            disabled={disableSubmit}
          >
            Create a post
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

const styles = {
  cardContent: {
    display: "flex",
    flexDirection: "column",
    gap: 2
  },
  renderValueBox: {
    display: "flex",
    flexWrap: "wrap",
    gap: 0.5,
    maxHeight: 120,
    overflowY: "auto"
  },
  chipAvatar: {
    bgcolor: "transparent",
    width: 24,
    height: 24
  },
  accountChip: {
    maxWidth: 180
  },
  tabPanel: {
    flex: 1
  },
  submitActions: {
    display: "flex",
    justifyContent: "flex-end",
    mt: 1
  },
  tabWarnDot: {
    position: "relative",
    "&::after": {
      content: '""',
      position: "absolute",
      top: 6,
      right: 6,
      width: 8,
      height: 8,
      bgcolor: "warning.main",
      borderRadius: "50%"
    }
  }
};
